import BenchmarkRunView, { type IBenchmarkRunSummary } from '../../Assistant/BenchmarkRunView';
import { SHORT_SHA_LENGTH } from '../releaseLogic';

interface IReleaseBenchmarkPanelProps {
  /** The whole-suite run at the draft's target commit, or `null` when none exists. */
  run: IBenchmarkRunSummary | null;
  isLoading: boolean;
  hasError: boolean;
  /** The draft's target commit — shown so the operator can dispatch a run by hand if none landed. */
  targetSha: string;
  /** The scene's poll cadence, so the retry notice quotes the interval actually in use. */
  pollIntervalMs: number;
}

/**
 * The draft's assistant benchmark, as a column on the current-draft card.
 *
 * Dispatched when the draft was announced, so an absent run means that dispatch
 * failed rather than that one was never wanted — the copy says so, and names the
 * commit to re-run against.
 */
const ReleaseBenchmarkPanel = ({
  run,
  isLoading,
  hasError,
  targetSha,
  pollIntervalMs,
}: IReleaseBenchmarkPanelProps) => (
  <section aria-label="Assistant benchmark" className="border-border-primary flex flex-col gap-3 border p-4">
    <div className="flex flex-col gap-1">
      <h2 className="text-fg-tertiary text-xs font-medium uppercase tracking-wider">Assistant benchmark</h2>
      <p className="text-fg-quaternary text-[11px] leading-relaxed">
        Compares assistant behavior at the last release against this draft's target commit, dispatched when you
        announced it. The verdict is advisory — a regression doesn't block anything — but Publish waits for the run to
        finish.
      </p>
    </div>

    {/*
      `!hasError` is load-bearing: an errored query nulls `data`, so without it a
      transient blip renders "the dispatch didn't land" and sends the operator off
      to re-run a benchmark by hand instead of letting the poll retry.
    */}
    {run === null && !isLoading && !hasError ? (
      <div className="flex flex-col gap-1">
        <span className="text-fg-warning-primary text-xs">No benchmark for this commit.</span>
        <span className="text-fg-tertiary text-[11px] leading-relaxed">
          The dispatch at announce time didn't land. Run one from Assistant → Benchmarks with{' '}
          <span className="text-fg-secondary font-mono">{targetSha.slice(0, SHORT_SHA_LENGTH)}</span> as the head commit
          and it will appear here.
        </span>
      </div>
    ) : (
      <BenchmarkRunView run={run} hasQueryError={hasError} pollIntervalMs={pollIntervalMs} />
    )}
  </section>
);

export default ReleaseBenchmarkPanel;
